import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToWindowEdges } from "@dnd-kit/modifiers";
import {
    arrayMove,
    sortableKeyboardCoordinates,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import cn from "classnames";
import { DefaultTemplate } from "components/modal/templates";
import { bidPointTypes, bidPointTypesEnum } from "helpers/enums";
import { enumLabel } from "helpers/system";
import moment from "moment";
import React, { useMemo, useState } from "react";

const REORDERABLE_POINT_TYPES = [
    bidPointTypesEnum.LoadPoint,
    bidPointTypesEnum.UnloadPoint,
    bidPointTypesEnum.CustomPoint,
];

const isReorderablePoint = (point) => REORDERABLE_POINT_TYPES.includes(point.type);

const pointTitle = (point) => {
    const { city, village, county } = point.geozone || {};
    return city || village || county || "Точка без населенного пункта";
};

const getPlanStayDuration = (point) => {
    const { planEnterDate, planLeaveDate } = point;
    if (!planEnterDate || !planLeaveDate) {
        return null;
    }
    const duration = moment(planLeaveDate).diff(moment(planEnterDate));
    return Number.isFinite(duration) ? duration : null;
};

const getPlanLeaveDateForSlot = (point, slotPlanEnterDate) => {
    const planStayDuration = getPlanStayDuration(point);
    if (planStayDuration === null || !slotPlanEnterDate) {
        return null;
    }
    return moment(slotPlanEnterDate).add(planStayDuration, "milliseconds").format();
};

const SortablePoint = ({ point, index, selected, disabled }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: point.id,
        disabled,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 1 : undefined,
    };

    return (
        <li
            ref={setNodeRef}
            className={cn("card mb-2 p-0 position-relative", {
                "border-primary": selected,
                shadow: isDragging,
            })}
            style={style}
        >
            <div className="d-flex align-items-stretch">
                <button
                    {...attributes}
                    {...listeners}
                    aria-label={`Переместить точку ${index + 1}`}
                    className="btn btn-link text-muted d-flex align-items-center justify-content-center px-3 py-2 border-right rounded-0 flex-shrink-0"
                    disabled={disabled}
                    title="Перетащить точку"
                    type="button"
                >
                    <i aria-hidden="true" className="icon-uEAB4-arrows-up-down" />
                </button>
                <div className="d-flex align-items-start flex-grow-1 overflow-hidden py-2 px-3 text-left">
                    <span className="badge badge-light mt-1 mr-2 flex-shrink-0">{index + 1}</span>
                    <div className="flex-grow-1 overflow-hidden">
                        <strong className="d-block text-truncate">{pointTitle(point)}</strong>
                        <div className="small text-muted text-truncate">
                            {enumLabel(bidPointTypes, point.type)} · {point.geozone?.address || "Адрес не указан"}
                        </div>
                    </div>
                </div>
            </div>
        </li>
    );
};

const BidPointsOrderList = ({ bidPoints, selectedPointId, hasPlanEmptyMileageBidModule, onSave, close }) => {
    const initialPoints = useMemo(
        () => [...bidPoints].sort((previousPoint, nextPoint) => previousPoint.order - nextPoint.order),
        [bidPoints],
    );
    const initialReorderablePoints = useMemo(() => initialPoints.filter(isReorderablePoint), [initialPoints]);
    const [points, setPoints] = useState(initialReorderablePoints);
    const [isLoading, setIsLoading] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
    const pointIds = points.map((point) => point.id);
    const orderChanged = points.some((point, index) => point.id !== initialReorderablePoints[index]?.id);

    const customPointsOrderIsValid = (nextPoints) => {
        if (nextPoints[nextPoints.length - 1]?.type === bidPointTypesEnum.CustomPoint) {
            setValidationMessage("Произвольная точка не может быть последней в маршруте");
            return false;
        }
        if (nextPoints[0]?.type === bidPointTypesEnum.CustomPoint && !hasPlanEmptyMileageBidModule) {
            setValidationMessage("Произвольная точка не может быть первой в маршруте");
            return false;
        }
        return true;
    };

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) {
            return;
        }
        const oldIndex = points.findIndex((point) => point.id === active.id);
        const newIndex = points.findIndex((point) => point.id === over.id);
        if (oldIndex < 0 || newIndex < 0) {
            return;
        }
        const nextPoints = arrayMove(points, oldIndex, newIndex);
        if (!customPointsOrderIsValid(nextPoints)) {
            return;
        }
        setValidationMessage("");
        setPoints(nextPoints);
    };

    const save = () => {
        if (!orderChanged || isLoading) {
            return;
        }
        const resetAfterSaveFailure = () => {
            setPoints(initialReorderablePoints);
            setValidationMessage("");
            setIsLoading(false);
        };
        setIsLoading(true);
        let reorderablePointIndex = 0;
        const reorderedBidPoints = initialPoints.map((point) => {
            if (!isReorderablePoint(point)) {
                return point;
            }
            const reorderedPoint = points[reorderablePointIndex];
            reorderablePointIndex += 1;
            if (reorderedPoint.id === point.id) {
                return point;
            }
            return {
                ...reorderedPoint,
                order: point.order,
                planEnterDate: point.planEnterDate,
                secondaryPlanEnterDate: point.secondaryPlanEnterDate,
                planLeaveDate:
                    reorderedPoint.type === bidPointTypesEnum.CustomPoint
                        ? getPlanLeaveDateForSlot(reorderedPoint, point.planEnterDate)
                        : point.planLeaveDate,
                planEnterDateOffset: undefined,
                secondaryPlanEnterDateOffset: undefined,
                planLeaveDateOffset: undefined,
            };
        });
        Promise.resolve()
            .then(() => onSave(reorderedBidPoints))
            .then((result) => {
                if (result !== true) {
                    resetAfterSaveFailure();
                    return;
                }
                close();
            })
            .catch(resetAfterSaveFailure);
    };

    return (
        <>
            <div className={cn("modal-window__body text-left", { loading: isLoading })}>
                <p className="small text-muted text-center mb-3">
                    Перетащите точки загрузки, выгрузки или произвольные точки в нужном порядке.
                </p>
                <div className="alert alert-info small mb-3" role="note">
                    <strong className="d-block mb-1">Что изменится</strong>
                    Адрес и остальные данные перемещаются вместе с точкой. Плановые даты загрузки и выгрузки остаются у
                    позиции маршрута. Для произвольной точки время выезда пересчитывается с сохранением прежней
                    длительности нахождения; если его не было, оно не добавится.
                </div>
                <DndContext
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
                    onDragEnd={handleDragEnd}
                    sensors={sensors}
                >
                    <SortableContext disabled={isLoading} items={pointIds} strategy={verticalListSortingStrategy}>
                        <ol className="list-unstyled mb-0">
                            {points.map((point, index) => (
                                <SortablePoint
                                    key={point.id}
                                    disabled={isLoading}
                                    index={index}
                                    point={point}
                                    selected={point.id === selectedPointId}
                                />
                            ))}
                        </ol>
                    </SortableContext>
                </DndContext>
                {validationMessage && <div className="small text-danger mt-2">{validationMessage}</div>}
            </div>
            <div className="modal-window__footer modal-window__footer--right">
                <button className="btn btn-secondary btn-sm mr-2" disabled={isLoading} onClick={close} type="button">
                    Отменить
                </button>
                <button
                    className="btn btn-brand btn-sm"
                    disabled={!orderChanged || isLoading}
                    onClick={save}
                    type="button"
                >
                    Сохранить
                </button>
            </div>
        </>
    );
};

class BidPointsOrderModal extends DefaultTemplate {
    renderBody = () => <BidPointsOrderList {...this.props} />;

    renderFooter = () => null;
}

export default BidPointsOrderModal;
